import castArray from 'lodash/castArray';
import entries from 'lodash/entries';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import { pairValuesWithLabels, transformEnumsAtSchemaNode } from './operators';
import {
  formatDate,
  getSchemaAtCursor,
  localizationKeyPath,
  resolveTranslationRefs,
} from './utils';

export const DynamicFormReadOnlyFieldFormatter = {
  fallback: ({ formData }) => formData,
  TextInput: ({ formData }) => formData,
  DFGroup: ({ context, formData = [], translations }) => {
    const translation = get(
      translations,
      [...localizationKeyPath(context.keyPath), 'optionLabels'],
      {}
    );

    return formData
      .map(value =>
        get(translation, [value, 'label'], get(translation, value, value))
      )
      .join(', ');
  },
  FilterableSelect: ({ context, formData, translations }) => {
    const optionLabels = resolveTranslationRefs(
      get(translations, [
        ...localizationKeyPath(context.keyPath),
        'optionLabels',
      ]),
      translations
    );

    return optionLabels[formData] || formData;
  },
  NumberInput: ({ formData }) => formData,
  Checkbox: ({ formData, translations }) => {
    const { checkboxChecked, checkboxUnchecked } = translations.reviewDetails;
    return formData ? checkboxChecked : checkboxUnchecked;
  },
  CheckboxGroup: ({ context, formData, translations }) =>
    entries(formData)
      .filter(([, value]) => value !== false)
      .reduce((acc, [key], index) => {
        const translation = get(
          translations,
          [...localizationKeyPath(context.keyPath), key, 'label'],
          null
        );

        if (translation) return (index > 0 ? acc + ', ' : '') + translation;

        return acc;
      }, ''),
  Radio: ({ componentProps, formData }) => componentProps.options[formData],
  YesNoRadio: ({ formData, translations }) => {
    const { radioNo, radioYes } = translations.reviewDetails;
    return formData ? radioYes : radioNo;
  },
  Select: ({ context, formData, translations, validate }) => {
    const schemaNode = getSchemaAtCursor(context.schemaPath, validate.schema);

    const combineProperties = (acc, label, value) => ({
      ...acc,
      [value]: label,
    });

    const optionLabels = pairValuesWithLabels(
      {
        labels: resolveTranslationRefs(
          get(translations, [
            ...localizationKeyPath(context.keyPath),
            'optionLabels',
          ]),
          translations
        ),
        values: transformEnumsAtSchemaNode(schemaNode),
      },
      {},
      combineProperties
    );

    return optionLabels[formData] || formData;
  },
  TextArea: ({ formData }) => formData,
  DateInput: ({ componentProps, formData }) => {
    const dateInputFormatter = {
      fallback: 'do MMMM yyyy',
      'YYYY-MM': 'MMMM yyyy',
    };

    const format =
      dateInputFormatter[componentProps.format] || dateInputFormatter.fallback;

    return formatDate(formData, format);
  },
  TileGroup: ({ componentProps, context, formData = [], translations }) => {
    const groupTranslation = get(
      translations,
      localizationKeyPath(context.keyPath),
      {}
    );

    const mapTileGroupLabels = (data, translation) => value =>
      get(translation, [value, 'label']) ||
      get(translation, value) ||
      get(translation, ['optionLabels', value, 'label']) ||
      get(translation, [
        'optionLabels',
        findIndex(data.children, { props: { value } }),
        'label',
      ]) ||
      value;

    return castArray(formData)
      .map(mapTileGroupLabels(componentProps, groupTranslation))
      .join(', ');
  },
};
